import { inject, Injectable } from "@angular/core";
import { FgdsProject } from "@domain/project/fgds-project";
import { getRequiredFgdsProjectServices } from "@domain/project/fgds-service-definition";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanPlaceholder } from "@domain/project/floorplan/floorplan-placeholder";
import type { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProductData } from "@domain/project/product-data/product-data";
import { GroupSection } from "@domain/project/product-list/group-section";
import { calculateDiscountedTotal } from "@domain/project/product-list/product-list-discount.util";
import { AdditionalProductConfigSection, ProductListService } from "@domain/project/product-list/product-list.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject } from "@domain/project/project-guards";

@Injectable({
  providedIn: "root",
})
export class ProjectCostService {
  private productListService: ProductListService = inject(ProductListService);

  calculateServicesCosts(project: ScopedProject): number {
    if (!isFgdsProject(project)) return 0;
    return getRequiredFgdsProjectServices(project).reduce((result, { service }) => {
      return result + (isNaN(service.costs!) ? 0 : service.costs!);
    }, 0);
  }

  calculateTotalNetPrice(
    project: ScopedProject,
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[] = [],
  ): number {
    return this.calculateTotalProductPrice(groupSections, additionalSections) + this.calculateServicesCosts(project);
  }

  calculateTotalProductPrice(groupSections: GroupSection[], additionalSections: AdditionalProductConfigSection[] = []) {
    return (
      this.calculatePlacedSectionSubtotal(groupSections) +
      this.calculateCombinedAttachmentInstructionsSubtotal(groupSections, additionalSections) +
      this.calculateAdditionalProductsSubtotal(additionalSections)
    );
  }

  calculateTotalProductPriceForGroupedExport(
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[] = [],
  ) {
    return (
      this.calculatePlacedGroupSubtotal(groupSections) +
      this.calculateCombinedAttachmentInstructionsSubtotal(groupSections, additionalSections) +
      this.calculateAdditionalProductsSubtotal(additionalSections)
    );
  }

  calculateTotalAttachmentInstructionsPrice(instructions: ProductData[], useZrefPrice = false) {
    return instructions.reduce((sum, instruction) => {
      const price = useZrefPrice ? instruction.zrefPriceData?.price : instruction.priceData?.price;
      return price == null ? sum : sum + calculateDiscountedTotal(price, instruction.quantity, instruction.discount ?? 0);
    }, 0);
  }

  private calculatePlacedSectionSubtotal(groupSections: GroupSection[]) {
    return groupSections.reduce(
      (sum, groupSection) =>
        sum +
        groupSection.alarmDevices.subtotal +
        groupSection.gasWarningCenters.subtotal +
        groupSection.plasticSigns.subtotal +
        groupSection.signalElements.subtotal +
        groupSection.transmitters.subtotal,
      0,
    );
  }

  private calculatePlacedGroupSubtotal(groupSections: GroupSection[]) {
    return groupSections.reduce((sum, groupSection) => sum + groupSection.subTotal, 0);
  }

  private calculateAdditionalProductsSubtotal(additionalSections: AdditionalProductConfigSection[]) {
    return additionalSections.reduce((sum, section) => sum + section.subtotal, 0);
  }

  private calculateCombinedAttachmentInstructionsSubtotal(
    groupSections: GroupSection[],
    additionalSections: AdditionalProductConfigSection[],
  ) {
    return this.calculateTotalAttachmentInstructionsPrice(
      this.getCombinedAttachmentInstructions(groupSections, additionalSections),
    );
  }

  getAssemblyPosition(project: FgdsProject): number {
    return this.countAllProductsAmount(project) + 1;
  }

  getInstallationPosition(project: FgdsProject): number {
    let position = this.countAllProductsAmount(project) + 1;
    if (project.assembly.required) position += 1;
    return position;
  }

  getDocumentationPosition(project: FgdsProject): number {
    let position = this.countAllProductsAmount(project) + 1;
    if (project.assembly.required) position += 1;
    if (project.installation.required) position += 1;
    return position;
  }

  getEngineeringPosition(project: FgdsProject): number {
    let position = this.countAllProductsAmount(project) + 1;
    if (project.assembly.required) position += 1;
    if (project.installation.required) position += 1;
    if (project.documentation.required) position += 1;
    return position;
  }

  getAdditionalServicesPosition(project: FgdsProject): number {
    let position = this.countAllProductsAmount(project) + 1;
    if (project.assembly.required) position += 1;
    if (project.installation.required) position += 1;
    if (project.documentation.required) position += 1;
    if (project.engineering.required) position += 1;
    return position;
  }
  countAllProductsAmount(project: FgdsProject): number {
    return (
      project.gasWarningCenters.filter((gasWarningCenter) => gasWarningCenter.countPlacedProducts() > 0).length +
      project.transmitters.filter((transmitter) => transmitter.countPlacedProducts() > 0).length +
      project.alarmDevices.filter((alarmDevice) => alarmDevice.countPlacedProducts() > 0).length +
      project.signalElements.filter((signalElement) => signalElement.countPlacedProducts() > 0).length +
      project.plasticSigns.filter((plasticSign) => plasticSign.countPlacedProducts() > 0).length +
      project.cachedFloorplans
        .flatMap((floorplan: Floorplan) =>
          [
            floorplan.gasWarningCenterPlaceholders,
            floorplan.transmitterPlaceholders,
            floorplan.alarmDevicePlaceholders,
            floorplan.plasticSignPlaceholders,
            floorplan.signalElementPlaceholders,
          ].flat(),
        )
        .filter((placeholder: FloorplanPlaceholder) =>
          placeholder.products.some((placeholderProduct: PlaceholderProduct) => placeholderProduct.id),
        ).length +
      project.directBomEntries.filter((entry) => entry.quantity > 0).length
    );
  }

  private getCombinedAttachmentInstructions(groupSections: GroupSection[], additionalSections: AdditionalProductConfigSection[]) {
    const placedAttachmentInstructions = this.productListService.getAttachmentInstructionsForProject(groupSections);
    const additionalAttachmentInstructions =
      this.productListService.getAttachmentInstructionsForAdditionalSections(additionalSections);
    return Array.from(
      new Map(
        [...placedAttachmentInstructions, ...additionalAttachmentInstructions].map((attachmentInstruction) => [
          attachmentInstruction.id,
          attachmentInstruction,
        ]),
      ).values(),
    );
  }
}
